import { useEffect, useMemo, useState } from "react";
import {
  ChevronRight, ChevronDown, Loader2, FileText, Image as ImageIcon,
  File as FileIcon, Globe, ExternalLink, AlertCircle, Search,
} from "lucide-react";
import webScraperService, {
  WebScrapeDiscoveredItem,
  WebScraperItemType,
  WebScraperItemStatus,
  WebScraperFileType,
} from "../../../services/web-scraper.service";

interface CrawlTreeViewProps {
  jobId: string;
  seedUrl: string;
  refreshKey: number;
}

const PAGE_BATCH_SIZE = 200;
const MAX_ITEMS = 2000;

const itemStatusColors: Record<WebScraperItemStatus, string> = {
  [WebScraperItemStatus.DISCOVERED]: "bg-gray-100 text-gray-600",
  [WebScraperItemStatus.IMPORTING]:  "bg-blue-100 text-blue-700",
  [WebScraperItemStatus.IMPORTED]:   "bg-green-100 text-green-700",
  [WebScraperItemStatus.SKIPPED]:    "bg-yellow-100 text-yellow-700",
  [WebScraperItemStatus.FAILED]:     "bg-red-100 text-red-700",
};

const fileTypeColors: Partial<Record<WebScraperFileType, string>> = {
  [WebScraperFileType.PDF]:   "bg-red-100 text-red-700",
  [WebScraperFileType.DOCX]:  "bg-blue-100 text-blue-700",
  [WebScraperFileType.XLSX]:  "bg-green-100 text-green-700",
  [WebScraperFileType.PPTX]:  "bg-orange-100 text-orange-700",
  [WebScraperFileType.CSV]:   "bg-teal-100 text-teal-700",
  [WebScraperFileType.IMAGE]: "bg-purple-100 text-purple-700",
  [WebScraperFileType.ZIP]:   "bg-gray-100 text-gray-700",
};

function iconForType(type: WebScraperItemType) {
  switch (type) {
    case WebScraperItemType.PAGE:  return Globe;
    case WebScraperItemType.IMAGE: return ImageIcon;
    case WebScraperItemType.FILE:  return FileText;
    default: return FileIcon;
  }
}

function nodeLabel(item: WebScrapeDiscoveredItem): string {
  return item.file_name || item.page_title || "(untitled)";
}

interface TreeNodeData {
  item: WebScrapeDiscoveredItem;
  children: TreeNodeData[];
}

export default function CrawlTreeView({ jobId, seedUrl, refreshKey }: CrawlTreeViewProps) {
  const [items, setItems] = useState<WebScrapeDiscoveredItem[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      setError(null);
      try {
        const all: WebScrapeDiscoveredItem[] = [];
        let total = 0;
        let skip = 0;
        while (skip < MAX_ITEMS) {
          const result = await webScraperService.listItems(jobId, { skip, limit: PAGE_BATCH_SIZE });
          if (cancelled) return;
          total = result.total;
          all.push(...result.list);
          if (result.list.length < PAGE_BATCH_SIZE || all.length >= total) break;
          skip += PAGE_BATCH_SIZE;
        }
        if (!cancelled) {
          setItems(all);
          setTotalAvailable(total);
          setExpanded(new Set([seedUrl]));
        }
      } catch {
        if (!cancelled) setError("Failed to load the crawl tree");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [jobId, refreshKey, seedUrl]);

  const { roots, totalNodes } = useMemo(() => {
    const byParent = new Map<string, WebScrapeDiscoveredItem[]>();
    for (const item of items) {
      const parent = item.found_on_page_url || "";
      const list = byParent.get(parent) ?? [];
      list.push(item);
      byParent.set(parent, list);
    }

    const known = new Set(items.map(i => i.source_url));

    const rootList: WebScrapeDiscoveredItem[] = [];
    for (const item of items) {
      const parent = item.found_on_page_url || "";
      if (!parent || !known.has(parent)) {
        rootList.push(item);
      }
    }

    function buildSubtree(parentUrl: string, visited: Set<string>): TreeNodeData[] {
      const kids = byParent.get(parentUrl) ?? [];
      return kids
        .filter(k => !visited.has(k.source_url))
        .map(child => {
          const nextVisited = new Set(visited);
          nextVisited.add(child.source_url);
          return {
            item: child,
            children: buildSubtree(child.source_url, nextVisited),
          };
        });
    }

    const tree: TreeNodeData[] = rootList.map(r => ({
      item: r,
      children: buildSubtree(r.source_url, new Set([r.source_url])),
    }));

    tree.sort((a, b) => {
      if (a.item.source_url === seedUrl) return -1;
      if (b.item.source_url === seedUrl) return 1;
      return 0;
    });

    return { roots: tree, totalNodes: items.length };
  }, [items, seedUrl]);

  const filteredRoots = useMemo<TreeNodeData[]>(() => {
    if (!search.trim()) return roots;
    const q = search.toLowerCase();

    function matches(item: WebScrapeDiscoveredItem) {
      return (item.source_url ?? "").toLowerCase().includes(q)
          || (item.file_name ?? "").toLowerCase().includes(q)
          || (item.page_title ?? "").toLowerCase().includes(q);
    }

    function prune(node: TreeNodeData): TreeNodeData | null {
      const keptKids = node.children
        .map(prune)
        .filter((c): c is TreeNodeData => c !== null);
      if (matches(node.item) || keptKids.length > 0) {
        return { item: node.item, children: keptKids };
      }
      return null;
    }

    return roots.map(prune).filter((r): r is TreeNodeData => r !== null);
  }, [roots, search]);

  useEffect(() => {
    if (!search.trim()) return;
    const allUrls = new Set<string>();
    function collect(node: TreeNodeData) {
      allUrls.add(node.item.source_url);
      node.children.forEach(collect);
    }
    filteredRoots.forEach(collect);
    setExpanded(allUrls);
  }, [search, filteredRoots]);

  const toggle = (url: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(url) ? next.delete(url) : next.add(url);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    function collect(node: TreeNodeData) {
      all.add(node.item.source_url);
      node.children.forEach(collect);
    }
    roots.forEach(collect);
    setExpanded(all);
  };

  const collapseAll = () => {
    setExpanded(new Set(roots.map(r => r.item.source_url)));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Globe className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Crawl Tree</h2>
              <p className="text-xs text-gray-500">Hierarchy of pages and documents the crawl found, grouped by the page they were discovered on.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium px-2 py-1 rounded hover:bg-purple-50"
            >
              Expand all
            </button>
            <button
              onClick={collapseAll}
              className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-50"
            >
              Collapse all
            </button>
          </div>
        </div>

        <div className="mt-3 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by URL, file name or page title"
            className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {totalAvailable > MAX_ITEMS && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Showing the first {totalNodes.toLocaleString()} of {totalAvailable.toLocaleString()} items. Use the Discovered Items tab for the full list.
          </p>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
        </div>
      ) : error ? (
        <div className="p-10 text-center">
          <AlertCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : filteredRoots.length === 0 ? (
        <div className="p-10 text-center">
          <Globe className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {search.trim() ? `No items match "${search}"` : "No items to show yet"}
          </p>
        </div>
      ) : (
        <div className="py-2 max-h-[640px] overflow-y-auto chat-scrollbar">
          {filteredRoots.map(root => (
            <TreeNodeRow
              key={root.item._id}
              node={root}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TreeNodeRowProps {
  node: TreeNodeData;
  depth: number;
  expanded: Set<string>;
  onToggle: (url: string) => void;
}

function TreeNodeRow({ node, depth, expanded, onToggle }: TreeNodeRowProps) {
  const { item, children } = node;
  const isOpen = expanded.has(item.source_url);
  const hasChildren = children.length > 0;
  const Icon = iconForType(item.item_type);

  const indentPx = depth * 24;

  return (
    <>
      <div
        className="group relative flex items-start gap-3 py-2.5 pr-4 hover:bg-purple-50/40 transition-colors cursor-pointer"
        style={{ paddingLeft: `${indentPx + 16}px` }}
        onClick={() => hasChildren && onToggle(item.source_url)}
      >
        {/* Vertical tree guide lines for nested levels */}
        {Array.from({ length: depth }).map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className="absolute top-0 bottom-0 w-px bg-gray-200"
            style={{ left: `${i * 24 + 24}px` }}
          />
        ))}

        {/* Chevron */}
        <button
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(item.source_url); }}
          className={`w-5 h-5 flex items-center justify-center flex-shrink-0 rounded transition-colors ${
            hasChildren ? "text-gray-500 hover:bg-gray-200" : "text-transparent cursor-default"
          }`}
          type="button"
          tabIndex={hasChildren ? 0 : -1}
        >
          {hasChildren && (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </button>

        {/* Type icon */}
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
          item.item_type === WebScraperItemType.PAGE  ? "bg-indigo-50"
          : item.item_type === WebScraperItemType.IMAGE ? "bg-purple-50"
          : "bg-blue-50"
        }`}>
          <Icon className={`w-4 h-4 ${
            item.item_type === WebScraperItemType.PAGE  ? "text-indigo-600"
            : item.item_type === WebScraperItemType.IMAGE ? "text-purple-600"
            : "text-blue-600"
          }`} />
        </div>

        {/* Title + URL stack */}
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate" title={nodeLabel(item)}>
              {nodeLabel(item)}
            </span>
            <a
              href={item.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-purple-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Open in new tab"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <span className="text-[11px] text-gray-500 truncate font-mono" title={item.source_url}>
            {item.source_url}
          </span>
        </div>

        {/* Right side: badges + child count */}
        <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
          {item.file_type ? (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${fileTypeColors[item.file_type] || "bg-gray-100 text-gray-600"}`}>
              {item.file_type}
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
              {item.item_type}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${itemStatusColors[item.status] || "bg-gray-100 text-gray-600"}`}>
            {item.status}
          </span>
          {hasChildren && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
              {children.length}
            </span>
          )}
        </div>
      </div>

      {isOpen && children.map(child => (
        <TreeNodeRow
          key={child.item._id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}
